import type { MedplumClient } from "@medplum/core";
import type { Resource, ResourceType } from "@medplum/fhirtypes";
import { syncKey } from "@repo/api";
import type { ChangeMessage, SyncConfig } from "@tanstack/db";
import { BasicIndex } from "@tanstack/db";
import type { CollectionConfig } from "../core/types";
import type { CanRead } from "./collection-scope";

export interface FhirCollectionOptions<T extends Resource = Resource, D = unknown> {
  resourceType: ResourceType;
  /** Distinguishes independently mutable views of the same resource type. */
  collectionName?: string;
  medplum: MedplumClient;
  /**
   * When set, the collection stays registered but only fetches from the server
   * if `canRead(resourceType)` is true. This keeps a narrow-AccessPolicy session
   * (e.g. the patient portal) from firing a search the server would 403 — the
   * collection is still registered so consumers resolve it, just left empty.
   */
  canRead?: CanRead;
  searchParams?: Record<string, string>;
  matches?: (resource: T) => boolean;
  /**
   * Optional field derivation applied on every sync write.
   *
   * Projects scalar foreign-key columns out of FHIR's nested references
   * (`Encounter.appointment[].reference` → `visitId: string`) so that
   * live-query joins can use `eq()` on top-level columns.
   *
   * Derived fields are merged onto the stored row. Collections consuming
   * a derived config should widen their TypeScript type accordingly.
   */
  deriveFields?: (resource: T) => D;
}

/** FHIR collection utilities */
export interface FhirCollectionUtils<T extends Resource = Resource> {
  /** Index signature for TanStack DB CollectionImpl compatibility */
  // biome-ignore lint/suspicious/noExplicitAny: required to match TanStack DB's Fn type
  [key: string]: (...args: Array<any>) => any;
  derive: (resource: T) => T;
  /** Refetch all data from the server (invalidates Medplum cache) */
  refetch: () => Promise<void>;
  /** Insert a new record (for subscription create events) */
  insertRecord: (resource: T) => void;
  /** Update an existing record (for subscription update events) */
  updateRecord: (resource: T) => void;
  /** Delete the row this resource is keyed by (for subscription delete events) */
  deleteRecord: (resource: T) => void;
}

/**
 * Factory function to create a TanStack DB collection config for FHIR resources.
 * Collections are read-only; mutations will be handled by tRPC layer.
 *
 * Every row is keyed by `syncKey`: the identity its mapper writes, so an
 * optimistic row and the server's row share a key. Every sync path — fetch,
 * refetch, broadcast, returned records, deletes — computes the key the same way.
 */
export function createFhirCollectionConfig<T extends Resource, D = unknown>(
  options: FhirCollectionOptions<T, D>,
): CollectionConfig<T & D, string, FhirCollectionUtils<T & D>> {
  const { resourceType, medplum, canRead, searchParams = {}, deriveFields } = options;
  const applyDerive = (resource: T): T & D =>
    (deriveFields ? { ...resource, ...deriveFields(resource) } : resource) as T & D;
  const deriveForSync = (resource: T): (T & D) | undefined => {
    try {
      return applyDerive(resource);
    } catch (error) {
      console.error(
        `Skipping ${resource.resourceType}/${resource.id ?? "<unknown>"} after deriveFields failure: ${error instanceof Error ? error.message : String(error)}`,
        error,
      );
      return undefined;
    }
  };

  // Apply server state under active overlays so rollback emits a coherent query delta.
  const accepted = new Map<string, T & D>();
  let sequence = 0;
  const fetching = new Set<number>();
  const tombstones = new Map<string, { prior: (T & D) | undefined; sequence: number; confirmed: boolean }>();
  const retireTombstones = () => {
    for (const [key, tombstone] of tombstones) {
      if (tombstone.confirmed && ![...fetching].some((start) => start < tombstone.sequence)) tombstones.delete(key);
    }
  };
  const updatedAt = (resource: T | undefined) => {
    const value = Date.parse(resource?.meta?.lastUpdated ?? "");
    return Number.isNaN(value) ? Number.NEGATIVE_INFINITY : value;
  };
  const accepts = (resource: T) => {
    const current = accepted.get(syncKey(resource));
    if (!current) return true;
    if (resource.meta?.versionId && resource.meta.versionId === current.meta?.versionId) return false;
    return updatedAt(resource) >= updatedAt(current);
  };

  // Store sync helpers for refetch
  let syncHelpers: {
    begin: Parameters<SyncConfig<T & D, string>["sync"]>[0]["begin"];
    write: (message: ChangeMessage<T & D, string>) => void;
    commit: () => void;
  } | null = null;
  let isCleanedUp = false;

  const fetchData = async (tolerateFailure: boolean) => {
    if (isCleanedUp || !syncHelpers) return;

    const profile = medplum.getProfile();
    if (!profile) return;

    // Session can't read this resource type: leave the collection registered but
    // empty rather than firing a search the server would reject with a 403.
    if (canRead && !canRead(resourceType)) return;

    const { begin, write, commit } = syncHelpers;
    const atStart = new Map(accepted);
    const started = ++sequence;
    fetching.add(started);
    const helpers = syncHelpers;

    try {
      const resources: T[] = [];
      for await (const page of medplum.searchResourcePages(resourceType, searchParams)) {
        resources.push(...(page as unknown as T[]));
      }

      if (isCleanedUp || syncHelpers !== helpers) return;

      begin({ immediate: true });
      const present = new Set(resources.map(syncKey));
      for (const resource of resources) {
        const key = syncKey(resource);
        if (!resource.id || tombstones.has(key) || !accepts(resource)) continue;
        const derived = deriveForSync(resource);
        if (!derived) continue;
        write({ key, value: derived, type: "update" });
        accepted.set(key, derived);
      }
      for (const [key, value] of atStart) {
        if (!present.has(key) && accepted.get(key) === value) {
          write({ key, value, type: "delete" });
          accepted.delete(key);
          tombstones.set(key, { prior: value, sequence: ++sequence, confirmed: true });
        }
      }
      commit();
      for (const tombstone of tombstones.values()) {
        if (tombstone.sequence < started) tombstone.confirmed = true;
      }
    } catch (error) {
      console.error(`Sync error for ${resourceType}:`, error);
      if (!tolerateFailure) throw error;
    } finally {
      fetching.delete(started);
      retireTombstones();
    }
  };

  const sync: SyncConfig<T & D, string> = {
    // Subscription deliveries are always the full updated FHIR resource, never
    // a partial delta. Default `partial` merges via Object.assign, which silently
    // preserves fields the new resource doesn't include — and FHIR serialization
    // omits null/empty fields, so e.g. clearing a `note` produces a payload
    // without the `note` key. The merge then keeps the stale note. `full`
    // replaces the row outright, so cleared fields actually clear.
    rowUpdateMode: "full",
    sync: (params) => {
      const { markReady } = params;

      // Store helpers for refetch
      syncHelpers = {
        begin: params.begin,
        write: params.write,
        commit: params.commit,
      };
      isCleanedUp = false;

      // Initial fetch - invalidate cache to ensure fresh data
      medplum.invalidateSearches(resourceType);
      fetchData(true).then(() => {
        markReady();
      });

      return {
        cleanup: () => {
          isCleanedUp = true;
          syncHelpers = null;
          accepted.clear();
          tombstones.clear();
        },
      };
    },
  };

  // Manual refetch - invalidates cache first
  const refetch = async () => {
    medplum.invalidateSearches(resourceType);
    return fetchData(false);
  };

  const insertRecord = (resource: T & D) => {
    if (!syncHelpers || isCleanedUp || !resource.id || !accepts(resource)) return;
    if (canRead && !canRead(resourceType)) return;
    if (options.matches && !options.matches(resource)) {
      deleteRecord(resource);
      return;
    }
    const key = syncKey(resource);
    const tombstone = tombstones.get(key);
    if (
      tombstone &&
      (!resource.meta?.lastUpdated ||
        (resource.meta.versionId && resource.meta.versionId === tombstone.prior?.meta?.versionId) ||
        updatedAt(resource) <= updatedAt(tombstone.prior))
    )
      return;
    const derived = deriveForSync(resource);
    if (!derived) return;
    tombstones.delete(key);
    const { begin, write, commit } = syncHelpers;
    begin({ immediate: true });
    write({ key, value: derived, type: "update" });
    accepted.set(key, derived);
    commit();
  };
  const updateRecord = insertRecord;

  const deleteRecord = (resource: T & D) => {
    if (!syncHelpers || isCleanedUp) return;
    const key = syncKey(resource);
    if (!key) return;
    const current = accepted.get(key);
    const prior = resource;
    if (current && updatedAt(prior) < updatedAt(current)) return;
    const previous = tombstones.get(key);
    if (previous && updatedAt(prior) <= updatedAt(previous.prior)) return;
    const { begin, write, commit } = syncHelpers;
    begin({ immediate: true });
    write({ key, value: prior as T & D, type: "delete" });
    accepted.delete(key);
    tombstones.set(key, { prior, sequence: ++sequence, confirmed: false });
    commit();
  };

  // Utils for mutations
  const utils: FhirCollectionUtils<T & D> = {
    derive: applyDerive,
    refetch,
    insertRecord,
    updateRecord,
    deleteRecord,
  };

  return {
    id: `fhir:${resourceType}${options.collectionName ? `:${options.collectionName}` : ""}`,
    getKey: syncKey,
    sync,
    utils,
    autoIndex: "eager",
    defaultIndexType: BasicIndex,
  };
}
